import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRO_TIER_ENABLED, PRO_FEATURES, type VaultFeatureKey } from "@/lib/featureFlags";
import { useSubscription } from "@/hooks/useSubscription";

interface ProGateRouteProps {
  featureKey: VaultFeatureKey;
  children: ReactNode;
}

/**
 * Route-level Pro gate that does NOT mount children when blocked.
 * Unlike ProGate (which renders children behind a blur overlay),
 * this completely prevents child component mounting — avoiding
 * side effects like API calls, redirects, and state initialization.
 * 
 * Use this for route-level gating. Use ProGate for inline feature gating.
 */
export default function ProGateRoute({ featureKey, children }: ProGateRouteProps) {
  const { entitlements, isLoading } = useSubscription();
  const navigate = useNavigate();

  // Allow through if pro tier is disabled or user has any active tier
  if (!PRO_TIER_ENABLED || entitlements.tier !== "expired") {
    return <>{children}</>;
  }

  // Show nothing while loading subscription status
  if (isLoading) return null;

  const feature = PRO_FEATURES[featureKey];

  // Block — do NOT mount children
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6 text-center">
      <div className="rounded-full bg-primary/10 p-4">
        <Lock className="h-6 w-6 text-primary" />
      </div>
      <div>
        <h2 className="font-display text-xl font-semibold text-foreground">
          {feature?.label ?? "Premium Feature"}
        </h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-md">
          {feature?.description ?? "This feature requires a Vault subscription."}
        </p>
      </div>
      <Button onClick={() => navigate("/upgrade")} className="gap-2 mt-2">
        <Sparkles className="h-4 w-4" />
        Unlock Vault
      </Button>
    </div>
  );
}
